import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import UnstyledImage from "./image";
import { Link } from "gatsby";
import { _ } from "../utils/i18n";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightLong } from "@fortawesome/free-solid-svg-icons";

const BlogWrapperGrid = styled.div`
  width: calc(100% / ${({ columns }) => (columns ? columns : 3)});
  display: flex;
  position: relative;
  padding: 0.75rem;
  font-family: ${({ theme }) => theme.fontParagraphs.fontFamily};
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    width: 50%;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    width: 100%;
    min-width: 90%;
    padding: 0 0.5rem;
  }
`;
const Card = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  border-radius: 16px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.08);
`;
const BlogWrapper = styled.div`
  width: 100%;
  height: 450px;
  display: flex;
  flex-flow: row wrap;
  align-items: stretch;
  font-family: ${({ theme }) => theme.fontParagraphs.fontFamily};

  &:nth-child(even) {
    flex-flow: row-reverse wrap;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    min-width: 90%;
    height: 100%;
  }
`;
const ImageWrapper = styled.div`
  width: 50%;
  display: flex;
  flex: 1;
  height: 100%;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    width: 100%;
    flex: unset;
    max-height: 250px;
  }
`;
const ImageLink = styled((props) => <Link {...props} />)`
  height: 300px;
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    height: 100%;
  }
`;
const Image = styled(UnstyledImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease-in-out !important;

  &:hover {
    transform: scale(1.04);
    cursor: pointer;
  }
`;
const TextDiv = styled.div`
  width: 50%;
  padding: 1rem 0;
  background-color: ${({ theme }) => theme.colors.secondary};
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    width: 100%;
    flex: unset;
  }
`;
const TextDivGrid = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin: 0;
  padding-bottom: 1rem;
  /* Keep "Les mer" aligned at the bottom of equal-height cards */
  > a:last-child {
    margin-top: auto;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    padding-inline: 1rem;
  }
`;
const Date = styled.p`
  color: ${({ theme }) => theme.colors.lightGray};
  font-size: 0.8rem;
  margin-top: 1rem;
  padding-inline: 1rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    padding-inline: 0;
  }
`;
const Title = styled.h2`
  color: ${({ theme }) => theme.colors.darkGray};
  font-weight: 300;
  font-size: clamp(1rem, 8vw, 1.3rem) !important;
  margin-block: 1rem;
  padding-inline: 1rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    padding-inline: 0;
  }
`;
const Author = styled.p`
  color: ${({ theme }) => theme.colors.lightGray};
  font-size: 0.8rem;
  padding-inline: 1rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    padding-inline: 0;
  }
`;
const Excerpt = styled.div`
  margin: 10px 0;
  line-height: 1.5;
  p {
    padding-inline: 1rem;
    font-size: 1rem;
  }
`;
const StyledLink = styled((props) => <Link {...props} />)`
  padding-inline: 1rem;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.darkGray};
  font-family: ${({ theme }) => theme.fontLinks.fontFamily};
  font-size: 15px;
  width: fit-content;
  @media (hover: hover) {
    &:hover {
      text-decoration: underline;
      svg {
        margin-left: 0.75rem;
      }
    }
  }
  span {
    margin-top: 5px;
    transition: all 0.3s ease;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    margin: 15px 0;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    padding-inline: 0;
  }
`;
const ArrowIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.colors.primary};
  margin-left: 0.25rem;
  transition: all 0.3s ease;
`;

export default function BlogCard({
  imgSrc,
  date,
  title,
  author,
  displayAuthor,
  displayFeaturedImage,
  displayPostContent,
  displayPostDate,
  excerpt,
  link,
  imgAlt,
  image,
  layout,
  columns,
  excerptLength,
}) {
  const truncate = (str) => {
    const words = str.split(" ");
    return words.length > excerptLength
      ? words.slice(0, excerptLength).join(" ") + "..."
      : str.replace("[&hellip;]", "...");
  };
  return layout === "list" ? (
    <BlogWrapper itemScope itemType="https://schema.org/Blog" columns={columns}>
      {displayFeaturedImage && (
        <ImageWrapper>
          <UnstyledImage
            alt={imgAlt}
            src={imgSrc}
            image={image}
            itemProp="image"
          />
        </ImageWrapper>
      )}
      <TextDiv>
        {displayPostDate && (
          <Date itemProp="datePublished">
            {_("Publisert")}: {date}
          </Date>
        )}
        <Title itemProp="name">{title}</Title>
        {displayAuthor && <Author itemProp="author">{author}</Author>}
        {displayPostContent && (
          <Excerpt
            itemProp="about"
            dangerouslySetInnerHTML={{ __html: truncate(excerpt) }}
          />
        )}

        {link && (
          <StyledLink to={link} itemProp="url">
            {_("Les mer")}
            <span>
              <ArrowIcon icon={faArrowRightLong} size="lg" />
            </span>
          </StyledLink>
        )}
      </TextDiv>
    </BlogWrapper>
  ) : (
    <BlogWrapperGrid
      itemScope
      itemType="https://schema.org/Blog"
      columns={columns}
    >
      <Card>
        {displayFeaturedImage && (
          <ImageLink to={link}>
            <Image alt={imgAlt} src={imgSrc} image={image} itemProp="image" />
          </ImageLink>
        )}
        <TextDivGrid>
          {displayPostDate && (
            <Date itemProp="datePublished">
              {_("Publisert")}: {date}
            </Date>
          )}
          <Title itemProp="name">{title}</Title>
          {displayAuthor && <Author itemProp="author">{author}</Author>}
          {displayPostContent && (
            <Excerpt
              itemProp="about"
              dangerouslySetInnerHTML={{ __html: truncate(excerpt) }}
            />
          )}

          {link && (
            <StyledLink to={link} itemProp="url">
              {_("Les mer")} <ArrowIcon icon={faArrowRightLong} size="lg" />
            </StyledLink>
          )}
        </TextDivGrid>
      </Card>
    </BlogWrapperGrid>
  );
}

BlogCard.propTypes = {
  imgSrc: PropTypes.object,
  date: PropTypes.string,
  title: PropTypes.string,
  author: PropTypes.string,
  displayAuthor: PropTypes.bool,
  displayFeaturedImage: PropTypes.bool,
  displayPostContent: PropTypes.bool,
  displayPostDate: PropTypes.bool,
  excerpt: PropTypes.string,
  link: PropTypes.string,
  imgAlt: PropTypes.string,
  image: PropTypes.object,
  columns: PropTypes.number,
  layout: PropTypes.string,
  excerptLength: PropTypes.number,
};
BlogCard.defaultProps = {
  imgSrc: undefined,
  date: undefined,
  title: undefined,
  author: undefined,
  displayAuthor: false,
  displayFeaturedImage: false,
  displayPostContent: false,
  displayPostDate: false,
  excerpt: undefined,
  link: undefined,
  imgAlt: undefined,
  image: undefined,
  columns: undefined,
  layout: "list",
  excerptLength: 100,
};
